import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";

const Steps = ({ d }) => {
  const dom = d || "craftendemic.com";
  const steps = [
    ["Simpan nama domain", `Ketik domain Anda di atas (contoh: ${dom}) lalu klik Simpan. Domain ini dipakai sebagai alamat utama toko.`],
    ["Publikasikan toko (Deploy)", "Di Emergent, klik tombol Deploy agar toko memiliki versi live."],
    ["Hubungkan domain di Emergent", `Buka Deployments → Link domain / Custom domain, lalu masukkan ${dom}. Emergent menampilkan record DNS yang harus dipasang.`],
    ["Pasang record DNS di registrar", `Login ke tempat Anda membeli domain (Niagahoster, Rumahweb, GoDaddy, Cloudflare, dll). Tambahkan persis record yang diberikan Emergent — biasanya record A untuk "@" (${dom}) dan CNAME untuk "www". Hapus record A/CNAME lama yang bentrok.`],
    ["Tunggu propagasi", "Perubahan DNS biasanya aktif dalam 5 menit – 24 jam. SSL (https) dibuat otomatis setelah domain terhubung."],
    ["Cek koneksi", "Klik \"Cek koneksi\" di halaman ini. Jika semua hijau, domain sudah tersambung."],
  ];
  return (
    <ol className="space-y-5" data-testid="domain-steps">
      {steps.map(([t, b], i) => (
        <li key={t} className="flex gap-4">
          <span className="font-mono text-sm text-[#C47A37] w-6 shrink-0">0{i + 1}</span>
          <div><p className="font-medium">{t}</p><p className="text-sm text-[#6E6259] leading-relaxed">{b}</p></div>
        </li>
      ))}
    </ol>
  );
};

const Status = ({ ok, label }) => (
  <p className="flex items-center gap-2 text-sm">{ok ? <CheckCircle2 size={16} className="text-[#2E6B40]" /> : <XCircle size={16} className="text-[#A23B2A]" />}{label}</p>
);

export default function Domain() {
  const [domain, setDomain] = useState("");
  const [saved, setSaved] = useState("");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => { setDomain(r.data.custom_domain || ""); setSaved(r.data.custom_domain || ""); setLoaded(true); }); }, []);
  const save = async () => {
    try { const r = await api.put("/admin/domain", { domain }); setDomain(r.data.domain); setSaved(r.data.domain); setCheck(null); toast.success("Domain disimpan"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const run = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/domain/check"); setCheck(r.data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-domain">
      <AdminTitle>Domain</AdminTitle>
      <div className="bg-white border border-[#E6DFD5] p-6 space-y-4">
        <Field label="Nama domain (tanpa https://), bisa diganti kapan saja">
          <input disabled={!loaded} value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="craftendemic.com" className={inputCls} data-testid="domain-input" />
        </Field>
        <div className="flex flex-wrap gap-3">
          <Btn onClick={save} data-testid="domain-save-btn">Simpan</Btn>
          <button onClick={run} disabled={!saved || busy} data-testid="domain-check-btn" className="border border-[#3B220C] px-5 py-2.5 text-sm disabled:opacity-40">{busy ? "Mengecek..." : "Cek koneksi"}</button>
        </div>
        {check && (
          <div className="border-t border-[#E6DFD5] pt-4 space-y-2" data-testid="domain-check-result">
            <Status ok={check[check.domain]?.resolves} label={`${check.domain} ${check[check.domain]?.resolves ? `mengarah ke ${check[check.domain].ips.join(", ")}` : "belum mengarah ke server mana pun"}`} />
            <Status ok={check[`www.${check.domain}`]?.resolves} label={`www.${check.domain} ${check[`www.${check.domain}`]?.resolves ? "sudah aktif" : "belum aktif"}`} />
            <Status ok={check.live} label={check.live ? "Toko Craftendemic sudah tampil di domain ini" : "Toko belum tampil di domain ini (lanjutkan langkah 2–5)"} />
          </div>
        )}
      </div>
      <div className="bg-white border border-[#E6DFD5] p-6 space-y-5">
        <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">CARA MENYAMBUNGKAN DOMAIN</p>
        <Steps d={saved || domain} />
        <p className="text-sm text-[#6E6259] border-t border-[#E6DFD5] pt-4"><b>Ganti domain:</b> lepas domain lama di Emergent (Deployments → domain → Remove), hubungkan domain baru dengan langkah 3–5, lalu simpan nama domain baru di atas.</p>
      </div>
    </div>
  );
}
